import React, { useCallback, useMemo, useState } from 'react';
import { TextInput } from '@fabric-ui/components';
import { Plus } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, Divider, useTheme } from '@mui/material';
import { CustomPicker, InjectedColorProps } from 'react-color';
import { Alpha, Hue, Saturation } from 'react-color/lib/components/common';
import tinycolor, { ColorFormats } from 'tinycolor2';
import { ColorItem } from './ColorItem';

const inputSymbol = '%';
export interface AvColorPickerProps extends InjectedColorProps {
  hexCode: string;
  onChangeHexCode: (color: string) => void;
}

interface Color {
  hsl: ColorFormats.HSLA;
  hsv: ColorFormats.HSVA;
  rgb: ColorFormats.RGBA;
  hex: string;
}

const COLORS_LOCAL_STORAGE = 'color-picker-recent-colors';

const extractAlpha = percentage => (percentage.split('%')?.[0] || 100) / 100;

const AvColorPickerBase: React.FC<AvColorPickerProps> = ({ hexCode, onChangeHexCode }) => {
  const theme = useTheme();
  const color = tinycolor(hexCode);
  const [colorState, setColorState] = useState<Color>({
    hsl: color.toHsl(),
    hsv: color.toHsv(),
    rgb: color.toRgb(),
    hex: hexCode,
  });

  const handleChange = useCallback(v => {
    const newColor = tinycolor(v);
    setColorState({
      hsl: newColor.toHsl(),
      hsv: newColor.toHsv(),
      rgb: newColor.toRgb(),
      hex: `#${newColor.toHex8()}`.toUpperCase(),
    });
    if (hexCode !== newColor.toHex8()) {
      onChangeHexCode(`#${newColor.toHex8()}`.toUpperCase());
    }
  }, []);

  const [localStorageColors, setLocalStorageColors] = useState<string[]>(JSON.parse(localStorage.getItem(COLORS_LOCAL_STORAGE) || '[]'));

  const addColor = color => {
    if (!localStorageColors.includes(color)) {
      const updatedColors = [color, ...localStorageColors].slice(0, 7);
      setLocalStorageColors(updatedColors); // Update state
      localStorage.setItem(COLORS_LOCAL_STORAGE, JSON.stringify(updatedColors)); // Update localStorage
    }
  };

  const defaultColors = useMemo(() => getDefaultColors(theme), [theme]);
  return (
    <Box sx={inlineStyles.container}>
      <Box sx={inlineStyles.saturation}>
        <Saturation {...(colorState as any)} pointer={CustomPointer} onChange={handleChange} />
      </Box>
      <Box sx={inlineStyles.sliderContainer}>
        <Hue {...(colorState as any)} pointer={CustomPointer} onChange={handleChange} />
      </Box>
      <Box sx={inlineStyles.sliderContainer}>
        <Alpha {...(colorState as any)} pointer={CustomPointer} onChange={handleChange} />
      </Box>
      <Box sx={{ ...flex.row, gap: 1 }}>
        <Box sx={{ fontSize: 12, fontWeight: 600, ...flex.itemsCenter }}>Hex</Box>
        <TextInput sx={inlineStyles.inputStyle} value={colorState.hex} onChange={e => handleChange(e)} size="xSmall" disableClearButton />
        <TextInput
          sx={inlineStyles.inputAlphaStyle}
          value={colorState.rgb.a * 100}
          onChange={percentage => handleChange({ ...colorState.rgb, a: extractAlpha(percentage) })}
          size="xSmall"
          disableClearButton
          inputProps={{ endAdornment: <div>{inputSymbol}</div> }}
        />
      </Box>
      <Box sx={{ ...flex.justifyBetweenCenter }}>
        <Box sx={{ fontSize: 12 }}>Saved colors:</Box>
        <Button size="xSmall" onClick={() => addColor(hexCode)} disabled={defaultColors.includes(hexCode.toUpperCase())}>
          <Plus style={iconSize(16)} />
          Add
        </Button>
      </Box>
      <Box
        sx={{
          ...flex.justifyBetweenStart,
          width: '100%',
        }}>
        {defaultColors.map(color => (
          <ColorItem key={color + generateUUID()} item={color} onClick={() => handleChange(color)} isActive={hexCode === color} />
        ))}
      </Box>
      {localStorageColors.length > 0 && (
        <>
          <Divider orientation="horizontal" />
          <Box
            sx={{
              ...flex.itemsStart,
              width: '100%',
            }}>
            {localStorageColors.map(color => (
              <ColorItem key={color + generateUUID()} item={color} onClick={() => handleChange(color)} isActive={hexCode === color} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

const getDefaultColors = theme =>
  [
    theme.palette.colors.negative[500],
    theme.palette.colors.orange[500],
    theme.palette.colors.yellow[500],
    theme.palette.colors.positive[500],
    theme.palette.colors.primary[500],
    theme.palette.colors.cyan[500],
    theme.palette.colors.purple[500],
  ].map(color => `${color}ff`.toUpperCase());

const borderRadius = '90px';
const inlineStyles = {
  container: {
    boxShadow: '0px 4px 20px 0px rgba(57, 58, 86, 0.15)',
    borderRadius: 1,
    ...flex.col,
    gap: 1.5,
    p: 2,
    minHeight: 370,
    width: 240,
  },
  pointer: {
    width: '10px',
    height: '10px',
    transform: 'translate(-3px,-1px)',
    borderRadius,
    cursor: 'pointer',
    border: theme => `2px solid ${theme.palette.colors.neutrals[100]}`,
    backgroundColor: 'transparent',
    boxShadow: '0px 0px 6px 0px rgba(57, 58, 86, 0.30)',
  },
  sliderContainer: { minHeight: 8, position: 'relative', '> div > div': { borderRadius } },
  saturation: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
    borderRadius: 0.5,
    overflow: 'hidden',
  },
  inputStyle: {
    width: 120,
  },
  inputAlphaStyle: {
    width: 65,
  },
};

const CustomPointer = () => <Box sx={inlineStyles.pointer} />;
const generateUUID = () => crypto.randomUUID();
export const AvColorPicker = CustomPicker(AvColorPickerBase);
